/**
 * @fileoverview Custom 3D Volume Rendering Presets Engine (v6.2.0 - Quantum Architecture)
 * @module MikhmelViewer/Presets3D
 * 
 * Enterprise Features:
 *   ✓ Strict TypeScript & ReadonlyDeep Guarantees
 *   ✓ Result Pattern (Either Monad) for Error Handling
 *   ✓ Encapsulated PresetRegistry (Singleton/Service)
 *   ✓ Safe VTK Adapter with Interface Segregation
 *   ✓ Reflect-based Deep Freeze with Collection Protection & Cycle Detection
 *   ✓ Runtime Validation on Init
 *   ✓ Dynamic Custom Presets Support
 *   ✓ Modality-based Filtering (CT/MRI/Any)
 */

import { log } from '../utils/logger.js';

// ══════════════════════════════════════════════════════════════════
// 1. TYPES & INTERFACES (Strict Duck Typing for VTK)
// ══════════════════════════════════════════════════════════════════

export type ColorPoint = readonly [hu: number, r: number, g: number, b: number];
export type OpacityPoint = readonly [hu: number, alpha: number];
export type GradientPoint = readonly [magnitude: number, alpha: number];

/**
 * [12.0.18] Фаза контрастирования, под которую посчитаны точки кривой.
 * Нужна подписям и авто-подбору: одни и те же органы в разные фазы имеют
 * РАЗНУЮ плотность (селезёнка: натив 40–55, портальная 90–120 HU).
 */
export type Phase = 'native' | 'arterial' | 'portal' | 'nephrographic' | 'excretory';

/** Что пресет намеренно давит до невидимости — для подписей и проверок. */
export type Suppressed = 'bone' | 'fat' | 'air' | 'muscle' | 'vessel' | 'collecting-system';

export interface Preset3D {
  readonly name: string;
  readonly category: string;
  readonly modality?: string;
  /**
   * [12.0.18] Необязательные поля-описания. Добавлены БЕЗ переноса схемы:
   * старые пресеты продолжают проходить validatePreset без единой правки,
   * потому что ни одно из этих полей не обязательно.
   */
  readonly phase?: Phase;
  readonly suppress?: readonly Suppressed[];
  /** Органы, на которые нацелен пресет — имена как в ai/organsMath.КЛАССЫ. */
  readonly organs?: readonly string[];
  readonly color: readonly ColorPoint[];
  readonly opacity: readonly OpacityPoint[];
  readonly gradient?: readonly GradientPoint[] | null;
  readonly opacityUnitDistance?: number;
  readonly shade?: boolean;
  readonly ambient?: number;
  readonly diffuse?: number;
  readonly specular?: number;
  readonly specularPower?: number;
}

// VTK Interfaces
interface IVtkTransferFunction {
  removeAllPoints(): void;
  addRGBPoint(x: number, r: number, g: number, b: number): void;
  addPoint(x: number, y: number): void;
}

interface IVtkVolumeProperty {
  getRGBTransferFunction?(index?: number): IVtkTransferFunction | IVtkTransferFunction[] | undefined;
  getScalarOpacity?(index?: number): IVtkTransferFunction | undefined;
  getGradientOpacity?(index?: number): IVtkTransferFunction | undefined;
  setUseGradientOpacity?(index: number, use: boolean): void;
  setShade?(shade: boolean): void;
  setAmbient?(val: number): void;
  setDiffuse?(val: number): void;
  setSpecular?(val: number): void;
  setSpecularPower?(val: number): void;
  setScalarOpacityUnitDistance?(index: number, distance: number): void;
}

interface IVtkVolumeActor {
  getProperty?(): IVtkVolumeProperty | undefined;
  property?: IVtkVolumeProperty;
}

interface IViewport {
  getDefaultActor?(): { actor: IVtkVolumeActor } | undefined;
  getActors?(): Array<{ actor: IVtkVolumeActor } | IVtkVolumeActor>;
  setProperties?(props: Record<string, unknown>): void;
  render(): void;
}

// Result Pattern (Monad)
export type Result<T, E = Error> = 
  | { readonly success: true; readonly value: T }
  | { readonly success: false; readonly error: E };

const Ok = <T>(value: T): Result<T, never> => ({ success: true, value });
const Err = <E>(error: E): Result<never, E> => ({ success: false, error });

// ══════════════════════════════════════════════════════════════════
// 2. ADVANCED UTILITIES
// ══════════════════════════════════════════════════════════════════

/**
 * Рекурсивный тип для глубокой заморозки.
 * Обеспечивает ReadonlyDeep гарантии на уровне типов для всех вложенных структур.
 */
type Primitive = string | number | boolean | bigint | symbol | undefined | null;

type ReadonlyDeep<T> = T extends Primitive
  ? T
  : T extends Function
    ? T
    : T extends Map<infer K, infer V>
      ? ReadonlyMap<ReadonlyDeep<K>, ReadonlyDeep<V>>
      : T extends Set<infer U>
        ? ReadonlySet<ReadonlyDeep<U>>
        : T extends (infer R)[]
          ? ReadonlyArray<ReadonlyDeep<R>>
          : T extends object
            ? { readonly [P in keyof T]: ReadonlyDeep<T[P]> }
            : T;

/**
 * Глубокая заморозка с использованием Reflect API.
 * Защищает от мутаций, обрабатывает Symbol-ы, циклические ссылки и блокирует Map/Set.
 */
const deepFreeze = <T extends object>(obj: T, visited = new WeakSet<object>()): ReadonlyDeep<T> => {
  if (obj === null || typeof obj !== 'object' || Object.isFrozen(obj)) {
    return obj as ReadonlyDeep<T>;
  }

  // Защита от циклических ссылок
  if (visited.has(obj)) {
    return obj as ReadonlyDeep<T>;
  }

  visited.add(obj);

  const keys = Reflect.ownKeys(obj) as Array<keyof T>;
  for (const key of keys) {
    const value = obj[key];
    if (value !== null && typeof value === 'object') {
      deepFreeze(value as object, visited);
    }
  }

  return Object.freeze(obj) as ReadonlyDeep<T>;
};

/**
 * Runtime валидация пресета
 */
function validatePreset(preset: Preset3D, key: string): void {
  if (!preset.name || typeof preset.name !== 'string') {
    throw new Error(`Preset "${key}": missing or invalid 'name'`);
  }
  
  if (!preset.category || typeof preset.category !== 'string') {
    throw new Error(`Preset "${key}": missing or invalid 'category'`);
  }
  
  if (!Array.isArray(preset.color) || preset.color.length === 0) {
    throw new Error(`Preset "${key}": 'color' must be a non-empty array`);
  }
  
  if (!Array.isArray(preset.opacity) || preset.opacity.length === 0) {
    throw new Error(`Preset "${key}": 'opacity' must be a non-empty array`);
  }
  
  // Проверка, что точки отсортированы по возрастанию
  for (let i = 1; i < preset.color.length; i++) {
    if (preset.color[i][0] <= preset.color[i - 1][0]) {
      throw new Error(`Preset "${key}": 'color' points must be sorted by HU value`);
    }
  }
  
  for (let i = 1; i < preset.opacity.length; i++) {
    if (preset.opacity[i][0] <= preset.opacity[i - 1][0]) {
      throw new Error(`Preset "${key}": 'opacity' points must be sorted by HU value`);
    }
  }
  
  if (preset.gradient && Array.isArray(preset.gradient)) {
    for (let i = 1; i < preset.gradient.length; i++) {
      if (preset.gradient[i][0] <= preset.gradient[i - 1][0]) {
        throw new Error(`Preset "${key}": 'gradient' points must be sorted by magnitude value`);
      }
    }
  }
  
  // Проверка диапазонов значений
  for (const [hu, r, g, b] of preset.color) {
    if (typeof hu !== 'number' || typeof r !== 'number' || typeof g !== 'number' || typeof b !== 'number') {
      throw new Error(`Preset "${key}": all color point values must be numbers`);
    }
    if (r < 0 || r > 1 || g < 0 || g > 1 || b < 0 || b > 1) {
      throw new Error(`Preset "${key}": RGB values must be in range [0, 1]`);
    }
  }
  
  for (const [hu, alpha] of preset.opacity) {
    if (typeof hu !== 'number' || typeof alpha !== 'number') {
      throw new Error(`Preset "${key}": all opacity point values must be numbers`);
    }
    if (alpha < 0 || alpha > 1) {
      throw new Error(`Preset "${key}": opacity values must be in range [0, 1]`);
    }
  }
}

// ══════════════════════════════════════════════════════════════════
// 3. CLINICAL PRESETS DEFINITION (Data Layer)
// ══════════════════════════════════════════════════════════════════

const _PRESETS_CONFIG: Record<string, Preset3D> = {

  // ────────────────────────── КОСТИ ──────────────────────────────

  'ct-bone-clean': {
    name: 'Кости (чистые)',
    category: 'bone',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [ 150,  0.50, 0.38, 0.28],
      [ 300,  0.82, 0.72, 0.55],
      [ 500,  0.92, 0.87, 0.72],
      [ 800,  0.97, 0.94, 0.84],
      [1200,  0.99, 0.97, 0.91],
      [3000,  1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ 100,  0.00],
      [ 150,  0.00],
      [ 180,  0.15],
      [ 220,  0.35],
      [ 300,  0.55],
      [ 450,  0.75],
      [ 700,  0.92],
      [1200,  0.98],
      [3000,  1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 15, 0.00],
      [ 40, 0.40],
      [100, 0.80],
      [300, 1.00],
    ],
    shade: true,
    ambient: 0.15,
    diffuse: 0.85,
    specular: 0.30,
    specularPower: 16,
  },

  'ct-bone-context': {
    name: 'Кости + контекст',
    category: 'bone',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  -50, 0.55, 0.40, 0.35],
      [   50, 0.72, 0.52, 0.45],
      [  200, 0.82, 0.72, 0.58],
      [  450, 0.92, 0.87, 0.72],
      [  800, 0.97, 0.94, 0.85],
      [ 2000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ -200, 0.00],
      [  -50, 0.005],
      [   40, 0.015],
      [  100, 0.015],
      [  200, 0.12],
      [  350, 0.50],
      [  550, 0.80],
      [  900, 0.94],
      [3000,  1.00],
    ],
    gradient: null,
    shade: true,
    ambient: 0.18,
    diffuse: 0.82,
    specular: 0.22,
    specularPower: 12,
  },

  'ct-bone-hard': {
    name: 'Кости (жёсткий)',
    category: 'bone',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [ 250,  0.90, 0.85, 0.70],
      [ 500,  0.96, 0.93, 0.82],
      [1000,  0.99, 0.97, 0.90],
      [3000,  1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ 249,  0.00],
      [ 255,  0.40],
      [ 400,  0.75],
      [ 650,  0.92],
      [1500,  1.00],
      [3000,  1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 20, 0.00],
      [ 60, 0.50],
      [200, 1.00],
    ],
    shade: true,
    ambient: 0.12,
    diffuse: 0.88,
    specular: 0.35,
    specularPower: 20,
  },

  'ct-bone-warm': {
    name: 'Кости (тёплые)',
    category: 'bone',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [ 200,  0.60, 0.35, 0.15],
      [ 400,  0.82, 0.55, 0.25],
      [ 650,  0.92, 0.75, 0.40],
      [1000,  0.98, 0.90, 0.60],
      [3000,  1.00, 1.00, 0.85],
    ],
    opacity: [
      [-1000, 0.00],
      [ 150,  0.00],
      [ 220,  0.08],
      [ 350,  0.45],
      [ 550,  0.78],
      [ 900,  0.94],
      [3000,  1.00],
    ],
    gradient: null,
    shade: true,
    ambient: 0.18,
    diffuse: 0.82,
    specular: 0.15,
    specularPower: 8,
  },

  // ────────────────────────── КОЖА ──────────────────────────────

  'ct-skin': {
    name: 'Кожа',
    category: 'skin',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [ -200, 0.68, 0.52, 0.40],
      [ -100, 0.78, 0.60, 0.48],
      [  -30, 0.86, 0.68, 0.55],
      [   30, 0.82, 0.62, 0.50],
      [  100, 0.75, 0.58, 0.48],
      [  500, 0.70, 0.70, 0.70],
    ],
    opacity: [
      [-1000, 0.00],
      [ -500, 0.00],
      [ -200, 0.00],
      [ -150, 0.40],
      [ -100, 0.60],
      [  -50, 0.65],
      [    0, 0.45],
      [   50, 0.20],
      [  100, 0.05],
      [  200, 0.00],
      [ 3000, 0.00],
    ],
    gradient: [
      [  0, 0.00],
      [  5, 0.00],
      [ 20, 0.30],
      [ 60, 0.85],
      [150, 1.00],
    ],
    shade: true,
    ambient: 0.28,
    diffuse: 0.72,
    specular: 0.10,
    specularPower: 5,
  },

  'ct-skin-transparent': {
    name: 'Кожа (прозрачная)',
    category: 'skin',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  -80, 0.75, 0.58, 0.45],
      [    0, 0.80, 0.62, 0.50],
      [  200, 0.85, 0.75, 0.62],
      [  500, 0.95, 0.92, 0.82],
      [ 1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ -200, 0.00],
      [ -100, 0.04],
      [  -30, 0.06],
      [   30, 0.03],
      [  150, 0.02],
      [  200, 0.08],
      [  250, 0.25],
      [  500, 0.60],
      [ 1000, 0.82],
      [ 3000, 0.90],
    ],
    gradient: [
      [  0, 0.00],
      [ 10, 0.00],
      [ 40, 0.60],
      [120, 1.00],
    ],
    shade: true,
    ambient: 0.25,
    diffuse: 0.75,
    specular: 0.15,
    specularPower: 8,
  },

  // ────────────────────────── МЫШЦЫ ─────────────────────────────

  'ct-muscle': {
    name: 'Мышцы',
    category: 'muscle',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  -80, 0.45, 0.30, 0.20],
      [    0, 0.55, 0.22, 0.18],
      [   30, 0.72, 0.28, 0.22],
      [   55, 0.82, 0.32, 0.24],
      [   80, 0.78, 0.35, 0.28],
      [  130, 0.70, 0.42, 0.35],
      [  300, 0.88, 0.82, 0.68],
      [ 1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ -150, 0.00],
      [  -80, 0.02],
      [  -30, 0.04],
      [    5, 0.10],
      [   25, 0.45],
      [   45, 0.70],
      [   65, 0.65],
      [   90, 0.40],
      [  140, 0.15],
      [  250, 0.08],
      [  500, 0.12],
      [ 1000, 0.12],
      [ 3000, 0.10],
    ],
    gradient: [
      [  0, 0.10],
      [ 10, 0.20],
      [ 30, 0.50],
      [ 80, 0.80],
      [200, 1.00],
    ],
    shade: true,
    ambient: 0.22,
    diffuse: 0.78,
    specular: 0.08,
    specularPower: 5,
  },

  // ────────────────────────── СОСУДЫ ────────────────────────────

  'ct-vascular': {
    name: 'Сосуды (КТА)',
    category: 'vascular',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  100, 0.40, 0.08, 0.06],
      [  160, 0.82, 0.16, 0.10],
      [  220, 0.95, 0.22, 0.12],
      [  320, 1.00, 0.35, 0.20],
      [  450, 1.00, 0.50, 0.30],
      [  600, 0.95, 0.80, 0.55],
      [ 1000, 1.00, 1.00, 0.90],
    ],
    opacity: [
      [-1000, 0.00],
      [  100, 0.00],
      [  130, 0.00],
      [  150, 0.20],
      [  200, 0.70],
      [  300, 0.88],
      [  420, 0.92],
      [  480, 0.35],
      [  550, 0.30],
      [  650, 0.55],
      [  800, 0.80],
      [ 1000, 0.90],
      [ 1500, 0.95],
      [ 3000, 1.00],
    ],
    gradient: null,
    opacityUnitDistance: 30.0,
    shade: true,
    ambient: 0.15,
    diffuse: 0.85,
    specular: 0.50,
    specularPower: 24,
  },

  'ct-vascular-bone': {
    name: 'Сосуды + кости',
    category: 'vascular',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  150, 0.82, 0.15, 0.12],
      [  300, 0.95, 0.28, 0.18],
      [  500, 0.88, 0.82, 0.65],
      [  800, 0.95, 0.92, 0.82],
      [ 2000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [  100, 0.00],
      [  150, 0.15],
      [  230, 0.65],
      [  380, 0.80],
      [  480, 0.25],
      [  550, 0.40],
      [  800, 0.70],
      [ 3000, 0.85],
    ],
    gradient: null,
    opacityUnitDistance: 18.0,
    shade: true,
    ambient: 0.15,
    diffuse: 0.85,
    specular: 0.30,
    specularPower: 14,
  },

  'ct-coronary': {
    name: 'Коронарные артерии',
    category: 'vascular',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  120, 0.60, 0.10, 0.08],
      [  200, 0.92, 0.22, 0.14],
      [  320, 1.00, 0.38, 0.24],
      [  450, 1.00, 0.90, 0.45],
      [  800, 0.90, 0.90, 0.80],
      [ 2000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [  110, 0.00],
      [  140, 0.12],
      [  200, 0.65],
      [  300, 0.85],
      [  400, 0.80],
      [  430, 0.70],
      [  480, 0.25],
      [  520, 0.15],
      [  600, 0.45],
      [  800, 0.88],
      [ 1200, 0.95],
      [ 3000, 1.00],
    ],
    gradient: null,
    opacityUnitDistance: 30.0,
    shade: true,
    ambient: 0.12,
    diffuse: 0.88,
    specular: 0.50,
    specularPower: 24,
  },

  // ────────────────────────── ДЫХАТЕЛЬНЫЕ ПУТИ ──────────────────

  'ct-airways': {
    name: 'Дых. пути (бронхи)',
    category: 'lung',
    modality: 'ct',
    color: [
      [-1000, 0.10, 0.20, 0.45],
      [ -950, 0.20, 0.35, 0.70],
      [ -850, 0.30, 0.50, 0.88],
      [ -700, 0.45, 0.62, 0.92],
      [ -300, 0.50, 0.50, 0.55],
      [    0, 0.35, 0.30, 0.30],
      [   50, 0.55, 0.48, 0.42],
    ],
    opacity: [
      [-1000, 0.00],
      [ -995, 0.00],
      [ -970, 0.08],
      [ -900, 0.40],
      [ -800, 0.60],
      [ -700, 0.45],
      [ -550, 0.15],
      [ -300, 0.05],
      [ -100, 0.08],
      [    0, 0.25],
      [   50, 0.45],
      [  100, 0.20],
      [  200, 0.02],
      [  500, 0.00],
      [ 3000, 0.00],
    ],
    gradient: null,
    opacityUnitDistance: 25.0,
    shade: true,
    ambient: 0.25,
    diffuse: 0.75,
    specular: 0.12,
    specularPower: 6,
  },

  'ct-organ-context': {
    name: 'Тело — фон для органов',
    category: 'general',
    modality: 'ct',
    // [12.0.30] ФОН ПОД ПОВЕРХНОСТИ ОРГАНОВ. Показывается, когда органная
    // кнопка рисует органы поверхностями (core/organSurface3D.js): орган без
    // тела врачу не нужен — «разве могут быть органы отдельно от организма?»
    //
    // Тело должно быть видно, но не закрывать орган. Поэтому:
    //   · кожа и мягкие ткани почти прозрачны, и держатся только их ГРАНИЦЫ
    //     (кривая по градиенту): выходит «стеклянный» силуэт, а не туман;
    //   · кость приглушена (0,3 вместо обычных 0,8): рёбра над печенью
    //     должны просвечивать, иначе орган снова не виден;
    //   · шаг накопления 12 мм — чтобы толща тела не набирала непрозрачность
    //     по пути (см. opacityUnitDistance в этом файле).
    // Числа подобраны по учебным диапазонам, на снимках владельца не мерены.
    //
    // [12.0.36] ЦВЕТ — ТЁПЛЫЙ, В ПАЛИТРЕ «ОБЗОРНОГО». Владелец: «почему органы
    // на сером фоне, а не на цветном, как в пресете «Обзорный»?» Серый был
    // выбором (цветные органы на нём выделялись), не необходимостью. Сам
    // «Обзорный» фоном быть не может: мягкие ткани у него непрозрачны на
    // 0,08–0,10, по толще стенки это копится в красный туман и закрывает
    // органы — на его скрине печень и почки еле видны. Поэтому меняется
    // только ЦВЕТ, прозрачность прежняя (её стережёт verify_12_0_30 §5).
    // [12.0.37] ФОН — «ОБЗОРНЫЙ». Владелец на 12.0.36: органы «опять на сером
    // фоне, а не на фоне, который представлен в пресете „Обзорный“». Тёплые
    // кожа и кость его не устроили: нужен сам «Обзорный» — живое тело.
    // Цвета — ровно его. Непрозрачность — его же кривая, но толща мягких
    // тканей и жира ослаблена втрое, кость и сосуды — вдвое: иначе по толще
    // стенки живота и петель кишки непрозрачность копится, и органы тонут в
    // красном тумане (на «Обзорном» печень и почки еле видны). Сквозь 5 см
    // мышцы орган виден на две трети — стережёт verify_12_0_37.
    suppress: ['fat'],
    color: [
      [-1000, 0.00, 0.00, 0.08],
      [ -700, 0.25, 0.20, 0.45],
      [ -100, 0.55, 0.40, 0.35],
      [   40, 0.70, 0.35, 0.30],
      [  200, 0.85, 0.62, 0.42],
      [  500, 0.95, 0.90, 0.75],
      [ 1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.000],
      [ -800, 0.007],
      [ -500, 0.014],
      [ -100, 0.010],
      [   30, 0.027],
      [  100, 0.034],
      [  250, 0.150],
      [  500, 0.300],
      [ 1000, 0.420],
      [ 3000, 0.500],
    ],
    gradient: null,
    opacityUnitDistance: 4.0,
    shade: true,
    ambient: 0.20,
    diffuse: 0.80,
    specular: 0.15,
    specularPower: 8,
  },
  'ct-lung-parenchyma': {
    name: 'Лёгкие (паренхима)',
    category: 'lung',
    modality: 'ct',
    // [12.0.26] ОТ −400 HU И ВЫШЕ — ПОЛНОСТЬЮ ПРОЗРАЧНО. Прежняя кривая
    // держала 0,68 при −50 и 0,82 при −200: это подкожный жир (−120…−80) и
    // мягкие ткани. Жир лёг бурой плёнкой на всё тело — первый скрин
    // владельца. Паренхима лёгкого −900…−600 остаётся. Тонкий «призрак» кожи
    // (частичный объём на границе воздух/кожа проходит через −900…−500) и
    // контуры газа в кишке порогом не убрать — чисто только маской долей.
    color: [
      [-1000, 0.05, 0.05, 0.10],
      [ -900, 0.25, 0.15, 0.20],
      [ -750, 0.55, 0.30, 0.38],
      [ -600, 0.72, 0.42, 0.48],
      [ -450, 0.78, 0.58, 0.52],
      [    0, 0.55, 0.40, 0.35],
    ],
    opacity: [
      [-1000, 0.00],
      [ -960, 0.00],               // воздух вокруг тела и в трахее
      [ -920, 0.04],
      [ -850, 0.22],
      [ -750, 0.32],               // паренхима лёгкого
      [ -600, 0.30],
      [ -500, 0.10],
      [ -400, 0.00],               // жир, мягкие ткани и кость — прозрачны
      [ 3000, 0.00],
    ],
    gradient: null,
    opacityUnitDistance: 20.0,
    shade: true,
    ambient: 0.22,
    diffuse: 0.78,
    specular: 0.06,
    specularPower: 4,
  },

  'ct-lung-surface': {
    name: 'Лёгкие (поверхность)',
    category: 'lung',
    modality: 'ct',
    color: [
      [-1000, 0.12, 0.12, 0.20],
      [ -800, 0.50, 0.32, 0.42],
      [ -600, 0.68, 0.45, 0.52],
      [ -400, 0.72, 0.52, 0.55],
      [ -200, 0.60, 0.48, 0.48],
    ],
    opacity: [
      [-1000, 0.00],
      [ -920, 0.00],
      [ -850, 0.10],
      [ -700, 0.30],
      [ -550, 0.50],
      [ -400, 0.35],
      [ -250, 0.10],
      [ -100, 0.01],
      [    0, 0.00],
      [ 3000, 0.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 10, 0.00],
      [ 40, 0.65],
      [120, 1.00],
    ],
    opacityUnitDistance: 20.0,
    shade: true,
    ambient: 0.22,
    diffuse: 0.78,
    specular: 0.15,
    specularPower: 8,
  },

  // ────────────────────────── ТОЛСТАЯ КИШКА ─────────────────────

  'ct-colon': {
    name: 'Толст. кишка',
    category: 'gi',
    modality: 'ct',
    color: [
      [-1000, 0.20, 0.20, 0.30],
      [ -800, 0.40, 0.38, 0.45],
      [ -200, 0.60, 0.52, 0.48],
      [    0, 0.75, 0.60, 0.50],
      [   80, 0.80, 0.55, 0.45],
      [  200, 0.70, 0.65, 0.55],
    ],
    opacity: [
      [-1000, 0.15],
      [ -950, 0.12],
      [ -600, 0.03],
      [ -200, 0.00],
      [  -50, 0.10],
      [   30, 0.40],
      [   70, 0.50],
      [  120, 0.20],
      [  200, 0.02],
      [  500, 0.00],
      [ 3000, 0.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 10, 0.00],
      [ 30, 0.60],
      [100, 1.00],
    ],
    shade: true,
    ambient: 0.30,
    diffuse: 0.70,
    specular: 0.10,
    specularPower: 6,
  },

  // ────────────────────────── MRI МОЗГ ──────────────────────────

  'mri-brain': {
    name: 'MRI Мозг',
    category: 'mri',
    modality: 'mri',
    color: [
      [  0,   0.00, 0.00, 0.00],
      [ 20,   0.22, 0.22, 0.28],
      [ 55,   0.45, 0.38, 0.48],
      [ 85,   0.68, 0.58, 0.58],
      [130,   0.82, 0.72, 0.68],
      [220,   0.92, 0.88, 0.82],
      [500,   1.00, 1.00, 1.00],
    ],
    opacity: [
      [  0,   0.00],
      [ 15,   0.00],
      [ 28,   0.05],
      [ 50,   0.25],
      [ 80,   0.50],
      [120,   0.65],
      [200,   0.55],
      [400,   0.40],
      [800,   0.35],
    ],
    gradient: null,
    shade: true,
    ambient: 0.22,
    diffuse: 0.78,
    specular: 0.08,
    specularPower: 6,
  },

  'mri-wm-gm': {
    name: 'WM/GM (Б/С вещество)',
    category: 'mri',
    modality: 'mri',
    color: [
      [  0,   0.00, 0.00, 0.00],
      [ 25,   0.12, 0.12, 0.18],
      [ 50,   0.50, 0.30, 0.55],
      [ 75,   0.55, 0.38, 0.62],
      [ 95,   0.75, 0.75, 0.90],
      [130,   0.88, 0.88, 0.95],
      [250,   1.00, 1.00, 1.00],
    ],
    opacity: [
      [  0,   0.00],
      [ 20,   0.00],
      [ 38,   0.10],
      [ 55,   0.38],
      [ 80,   0.55],
      [120,   0.60],
      [200,   0.40],
      [500,   0.25],
    ],
    gradient: [
      [  0, 0.00],
      [  5, 0.00],
      [ 20, 0.50],
      [ 60, 0.90],
      [150, 1.00],
    ],
    shade: true,
    ambient: 0.25,
    diffuse: 0.75,
    specular: 0.10,
    specularPower: 8,
  },

  // ────────────────────────── СЕРДЦЕ ────────────────────────────

  'cardiac-3d': {
    name: 'Сердце 3D',
    category: 'cardiac',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [ -100, 0.80, 0.65, 0.25],
      [   30, 0.60, 0.16, 0.12],
      [   60, 0.78, 0.22, 0.16],
      [  130, 0.90, 0.30, 0.20],
      [  220, 0.96, 0.40, 0.28],
      [  380, 1.00, 0.55, 0.38],
      [  800, 1.00, 0.90, 0.60],
      [ 1500, 1.00, 1.00, 0.90],
    ],
    opacity: [
      [-1000, 0.00],
      [ -200, 0.00],
      [ -100, 0.06],
      [  -50, 0.02],
      [   15, 0.00],
      [   35, 0.30],
      [   55, 0.55],
      [   80, 0.65],
      [  150, 0.78],
      [  250, 0.88],
      [  400, 0.90],
      [  470, 0.40],
      [  550, 0.55],
      [  750, 0.72],
      [ 1500, 0.85],
      [ 3000, 0.90],
    ],
    gradient: null,
    opacityUnitDistance: 30.0,
    shade: true,
    ambient: 0.16,
    diffuse: 0.84,
    specular: 0.40,
    specularPower: 18,
  },

  'cardiac-fat': {
    name: 'Сердце + жир',
    category: 'cardiac',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [ -120, 0.88, 0.78, 0.30],
      [  -60, 0.92, 0.82, 0.35],
      [   50, 0.72, 0.22, 0.18],
      [  200, 0.90, 0.30, 0.22],
      [  400, 0.95, 0.90, 0.50],
      [ 1000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ -150, 0.00],
      [ -120, 0.18],
      [  -60, 0.28],
      [  -20, 0.08],
      [   40, 0.20],
      [  150, 0.55],
      [  300, 0.72],
      [  480, 0.25],
      [  700, 0.04],
      [ 3000, 0.02],
    ],
    gradient: null,
    opacityUnitDistance: 28.0,
    shade: true,
    ambient: 0.20,
    diffuse: 0.80,
    specular: 0.20,
    specularPower: 10,
  },

  // ────────────────────────── АНГИОГРАФИЯ ───────────────────────

  'angio-3d': {
    name: 'Ангио 3D',
    category: 'vascular',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  120, 0.45, 0.10, 0.08],
      [  180, 0.85, 0.18, 0.12],
      [  280, 0.98, 0.28, 0.18],
      [  400, 1.00, 0.45, 0.30],
      [  550, 0.92, 0.62, 0.38],
    ],
    opacity: [
      [-1000, 0.00],
      [  100, 0.00],
      [  130, 0.00],
      [  155, 0.25],
      [  200, 0.72],
      [  300, 0.90],
      [  420, 0.92],
      [  500, 0.35],
      [  580, 0.03],
      [  700, 0.00],
      [ 3000, 0.00],
    ],
    gradient: null,
    opacityUnitDistance: 30.0,
    shade: true,
    ambient: 0.12,
    diffuse: 0.88,
    specular: 0.55,
    specularPower: 28,
  },

  // ────────────────────────── ПЛОД ──────────────────────────────

  'fetus': {
    name: 'Плод 3D',
    category: 'obstetric',
    modality: 'ct',
    // [12.0.26] СКЕЛЕТ ПЛОДА вместо поверхности. Прежняя кривая набирала
    // непрозрачность с 30 HU: у матери это брюшная стенка и матка (40–60),
    // и плод закрывался ими целиком. Поверхность плода (30–50 HU) от стенки
    // матки плотностью не отделить. Окостеневший скелет (150–1000 HU) виден
    // сквозь ткани матери — для этого КТ беременной и делают. Кости матери
    // (таз, позвоночник) будут в кадре. Проверить можно только на
    // исследовании беременной — на других сериях покажет обычный скелет.
    suppress: ['fat', 'muscle', 'air'],
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  150, 0.80, 0.68, 0.56],
      [  300, 0.92, 0.84, 0.72],   // окостеневающий скелет плода
      [  600, 0.98, 0.94, 0.86],
      [ 1500, 1.00, 1.00, 0.96],
    ],
    opacity: [
      [-1000, 0.00],
      [  150, 0.00],               // всё мягкое, включая стенку матки, прозрачно
      [  200, 0.20],
      [  300, 0.60],
      [  500, 0.85],
      [ 1000, 0.95],
      [ 3000, 0.95],
    ],
    gradient: [
      [  0, 0.00],
      [ 10, 0.00],
      [ 30, 0.60],
      [100, 1.00],
    ],
    shade: true,
    ambient: 0.30,
    diffuse: 0.70,
    specular: 0.08,
    specularPower: 4,
  },

  // ────────────────────────── СПЕЦИАЛЬНЫЕ ───────────────────────

  'ct-overview': {
    name: 'Обзорный VRT',
    category: 'special',
    modality: 'any',
    color: [
      [-1000, 0.00, 0.00, 0.08],
      [ -700, 0.25, 0.20, 0.45],
      [ -100, 0.55, 0.40, 0.35],
      [   40, 0.70, 0.35, 0.30],
      [  200, 0.85, 0.62, 0.42],
      [  500, 0.95, 0.90, 0.75],
      [ 1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ -800, 0.02],
      [ -500, 0.04],
      [ -100, 0.03],
      [   30, 0.08],
      [  100, 0.10],
      [  250, 0.30],
      [  500, 0.60],
      [ 1000, 0.85],
      [ 3000, 1.00],
    ],
    gradient: null,
    shade: true,
    ambient: 0.20,
    diffuse: 0.80,
    specular: 0.15,
    specularPower: 8,
  },

  'ct-xray': {
    name: 'Рентген (DRR)',
    category: 'special',
    modality: 'any',
    color: [
      [-1000, 1.00, 1.00, 1.00],
      [ 3000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.000],
      [ -600, 0.002],
      [ -200, 0.005],
      [    0, 0.008],
      [  100, 0.012],
      [  300, 0.040],
      [  700, 0.080],
      [ 1500, 0.120],
      [ 3000, 0.150],
    ],
    gradient: null,
    shade: false,
    ambient: 1.0,
    diffuse: 0.0,
    specular: 0.0,
    specularPower: 1,
  },

  'ct-density-map': {
    name: 'Карта плотности',
    category: 'special',
    modality: 'any',
    color: [
      [-1000, 0.00, 0.00, 0.08],
      [ -600, 0.00, 0.00, 0.55],
      [ -200, 0.00, 0.45, 0.78],
      [    0, 0.00, 0.78, 0.38],
      [   50, 0.55, 0.88, 0.00],
      [  200, 1.00, 0.78, 0.00],
      [  500, 1.00, 0.28, 0.00],
      [ 1000, 1.00, 0.00, 0.00],
      [ 3000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ -800, 0.02],
      [ -400, 0.05],
      [    0, 0.08],
      [   60, 0.12],
      [  200, 0.30],
      [  500, 0.60],
      [ 1000, 0.85],
      [ 3000, 1.00],
    ],
    gradient: null,
    shade: false,
    ambient: 1.0,
    diffuse: 0.0,
    specular: 0.0,
    specularPower: 1,
  },

  // ══════════════════════════════════════════════════════════════════
  //  EXPERIMENTAL PRESETS (15 исходных + 3 органа брюшной полости, [11.1.15])
  // ══════════════════════════════════════════════════════════════════

  'custom-01-ct-liver': {
    name: '[01] Печень и ОБП',
    category: 'gi',
    modality: 'ct',
    // [12.0.26] Числа — для ПОРТАЛЬНОЙ фазы: печень 100–120 HU, мышца 60–70.
    // На артериальной печень 60–80 HU — вровень с мышцей, порогом не
    // отделить; на нативе 45–65 — тем более. Там кнопка сама уходит в маску
    // (core/presetRouteCore.js). Селезёнка и почки той же и большей
    // плотности попадут в кадр вместе с печенью — это предел метода.
    phase: 'portal',
    organs: ['liver'],
    suppress: ['bone', 'fat', 'muscle', 'air'],
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   75, 0.55, 0.26, 0.20],
      [  105, 0.74, 0.38, 0.26],   // паренхима печени, портальная фаза
      [  150, 0.86, 0.55, 0.36],   // воротная и печёночные вены 150–180
      [  300, 0.90, 0.85, 0.75],
      [ 1000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      // [12.0.26] ДО 75 HU — ПОЛНОСТЬЮ ПРОЗРАЧНО. Кожа, мышцы брюшной стенки и
      // диафрагма — 40–60 HU (с контрастом мышца добирает до 60–70). Прежняя
      // кривая набирала непрозрачность с 25–45 HU, и луч гас в первых
      // сантиметрах стенки: владелец прислал скрины — «мумия», рёбра едва
      // просвечивают. Работающие «Почки» и «Клапаны» прозрачны до 60 и 130 HU —
      // отсюда правило. Порог взят с запасом над мышцей по учебным
      // диапазонам, не по серии владельца.
      [-1000, 0.00],
      [   75, 0.00],
      [   90, 0.25],
      [  105, 0.62],               // паренхима печени в портальную фазу
      [  130, 0.70],
      [  180, 0.45],               // сосуды печени и селезёнка — уже приглушённо
      [  260, 0.12],
      [  500, 0.04],
      [ 3000, 0.02],               // кость подавлена
    ],
    gradient: [
      [  0, 0.05],
      [ 10, 0.15],
      [ 40, 0.55],
      [100, 1.00],
    ],
    shade: true,
    ambient: 0.22,
    diffuse: 0.78,
    specular: 0.10,
    specularPower: 5,
  },

  'custom-02-ct-kidneys': {
    name: '[02] Почки (Урография)',
    category: 'gi',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   40, 0.55, 0.18, 0.08],
      [   80, 0.78, 0.38, 0.12],
      [  150, 0.90, 0.68, 0.18],
      [  300, 1.00, 0.88, 0.48],
      [  800, 1.00, 1.00, 0.80],
    ],
    opacity: [
      [-1000, 0.00],
      [   60, 0.00],
      [   80, 0.12],
      [  120, 0.62],
      [  200, 0.85],
      [  400, 0.38],
      [ 1000, 0.08],
    ],
    gradient: null,
    shade: true,
    ambient: 0.25,
    diffuse: 0.75,
    specular: 0.15,
    specularPower: 8,
  },

  'custom-03-ct-fat': {
    name: '[03] Жировая ткань',
    category: 'special',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [ -150, 0.68, 0.78, 0.28],
      [ -100, 0.78, 0.88, 0.38],
      [  -50, 0.88, 0.94, 0.48],
      [    0, 0.58, 0.38, 0.28],
      [  100, 0.48, 0.48, 0.48],
    ],
    opacity: [
      [-1000, 0.00],
      [ -200, 0.00],
      [ -140, 0.22],
      [ -100, 0.55],
      [  -60, 0.45],
      [  -30, 0.00],
      [ 3000, 0.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 20, 0.00],
      [ 50, 0.80],
      [100, 1.00],
    ],
    shade: true,
    ambient: 0.30,
    diffuse: 0.70,
    specular: 0.05,
    specularPower: 2,
  },

  'custom-04-ct-brain-vessels': {
    name: '[04] Сосуды мозга',
    category: 'vascular',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   60, 0.45, 0.08, 0.08],
      [  120, 0.78, 0.12, 0.18],
      [  200, 0.95, 0.18, 0.28],
      [  300, 1.00, 0.38, 0.48],
      [  600, 0.75, 0.75, 0.75],
    ],
    opacity: [
      [-1000, 0.00],
      [   80, 0.00],
      [  110, 0.12],
      [  160, 0.65],
      [  260, 0.85],
      [  360, 0.90],
      [  420, 0.08],
      [  600, 0.00],
      [ 1000, 0.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 20, 0.00],
      [ 60, 0.50],
      [180, 1.00],
    ],
    shade: true,
    ambient: 0.15,
    diffuse: 0.85,
    specular: 0.42,
    specularPower: 20,
  },

  'custom-05-ct-carotid': {
    name: '[05] Сонные артерии',
    category: 'vascular',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   80, 0.55, 0.08, 0.08],
      [  150, 0.88, 0.18, 0.12],
      [  300, 1.00, 0.48, 0.28],
      [  600, 0.88, 0.78, 0.68],
      [ 1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [   90, 0.00],
      [  130, 0.18],
      [  190, 0.65],
      [  320, 0.82],
      [  450, 0.25],
      [  600, 0.45],
      [ 1000, 0.65],
    ],
    gradient: null,
    shade: true,
    ambient: 0.18,
    diffuse: 0.82,
    specular: 0.30,
    specularPower: 12,
  },

  'custom-06-ct-dental': {
    name: '[06] Имплантология',
    category: 'bone',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  500, 0.78, 0.72, 0.62],
      [ 1000, 0.90, 0.88, 0.80],
      [ 1500, 0.95, 0.95, 0.90],
      [ 2500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [  400, 0.00],
      [  600, 0.12],
      [ 1000, 0.62],
      [ 1500, 0.92],
      [ 3000, 1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 50, 0.00],
      [150, 0.70],
      [300, 1.00],
    ],
    shade: true,
    ambient: 0.10,
    diffuse: 0.90,
    specular: 0.60,
    specularPower: 40,
  },

  'custom-07-ct-fracture': {
    name: '[07] Переломы',
    category: 'bone',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  200, 0.58, 0.58, 0.62],
      [  400, 0.78, 0.78, 0.82],
      [  800, 0.94, 0.94, 0.98],
      [ 1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [  150, 0.00],
      [  250, 0.22],
      [  450, 0.78],
      [  800, 0.92],
      [ 2000, 1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 25, 0.00],
      [ 80, 0.75],
      [250, 1.00],
    ],
    shade: true,
    ambient: 0.15,
    diffuse: 0.85,
    specular: 0.20,
    specularPower: 15,
  },

  'custom-08-ct-mip-pseudo': {
    name: '[08] Псевдо-MIP',
    category: 'special',
    modality: 'any',
    color: [
      [-1000, 1.00, 1.00, 1.00],
      [ 3000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [ -200, 0.00],
      [    0, 0.005],
      [  200, 0.020],
      [  500, 0.050],
      [ 1000, 0.080],
      [ 3000, 0.120],
    ],
    gradient: null,
    shade: false,
    ambient: 1.00,
    diffuse: 0.00,
    specular: 0.00,
    specularPower: 1,
  },

  'custom-09-mri-angio': {
    name: '[09] МР-ангио (TOF)',
    category: 'mri',
    modality: 'mri',
    color: [
      [   0, 0.00, 0.00, 0.00],
      [  50, 0.48, 0.08, 0.08],
      [ 150, 0.85, 0.18, 0.12],
      [ 300, 1.00, 0.38, 0.18],
      [ 600, 1.00, 0.78, 0.48],
      [1000, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [   0, 0.00],
      [  80, 0.00],
      [ 120, 0.18],
      [ 200, 0.62],
      [ 400, 0.88],
      [1000, 1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 10, 0.00],
      [ 40, 0.50],
      [100, 1.00],
    ],
    shade: true,
    ambient: 0.20,
    diffuse: 0.80,
    specular: 0.40,
    specularPower: 20,
  },

  'custom-10-mri-t2-fluid': {
    name: '[10] МРТ T2 (Жидкости)',
    category: 'mri',
    modality: 'mri',
    color: [
      [   0, 0.00, 0.00, 0.00],
      [ 100, 0.08, 0.18, 0.48],
      [ 200, 0.18, 0.38, 0.78],
      [ 400, 0.38, 0.68, 1.00],
      [ 800, 0.78, 0.88, 1.00],
      [1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [   0, 0.00],
      [ 150, 0.00],
      [ 250, 0.22],
      [ 400, 0.62],
      [ 800, 0.90],
      [1500, 1.00],
    ],
    gradient: null,
    shade: true,
    ambient: 0.30,
    diffuse: 0.70,
    specular: 0.10,
    specularPower: 5,
  },

  'custom-11-ct-cardiac-valves': {
    name: '[11] Клапаны (Кальций)',
    category: 'cardiac',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  150, 0.55, 0.08, 0.08],
      [  300, 0.78, 0.28, 0.18],
      [  500, 1.00, 0.78, 0.28],
      [  800, 1.00, 1.00, 0.58],
      [ 1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [  130, 0.00],
      [  180, 0.05],
      [  300, 0.22],
      [  450, 0.72],
      [  700, 0.92],
      [ 1500, 1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 20, 0.00],
      [ 70, 0.70],
      [200, 1.00],
    ],
    shade: true,
    ambient: 0.10,
    diffuse: 0.90,
    specular: 0.50,
    specularPower: 25,
  },

  'custom-12-ct-pulmonary-vessels': {
    name: '[12] Легочные сосуды',
    category: 'lung',
    modality: 'ct',
    // [12.0.26] Нужен контраст: лёгочные сосуды на артериальной фазе тела и
    // на КТ-ангиографии лёгочной артерии — 150–400 HU. Воздух лёгкого
    // прозрачен сам собой (−850), грудная стенка — по правилу ниже. Сердце и
    // аорта того же контраста попадут в кадр — отделить их может только
    // маска. На нативе сосуды 40 HU — вровень со стенкой, не показать.
    phase: 'arterial',
    suppress: ['air', 'fat', 'muscle', 'bone'],
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   90, 0.55, 0.08, 0.16],
      [  150, 0.78, 0.16, 0.18],
      [  250, 0.92, 0.32, 0.22],   // контрастированная кровь
      [  400, 1.00, 0.52, 0.32],
      [  700, 0.88, 0.88, 0.88],
    ],
    opacity: [
      // [12.0.26] Прозрачно до 90 HU: прежняя кривая набирала 0,15 уже при
      // 50 HU — это мышцы грудной стенки, они и закрывали всё.
      [-1000, 0.00],
      [   90, 0.00],
      [  130, 0.30],
      [  180, 0.72],               // сосуды с контрастом
      [  300, 0.85],
      [  450, 0.25],               // кортикальная кость — приглушённо
      [  800, 0.04],
      [ 3000, 0.02],
    ],
    gradient: [
      [  0, 0.00],
      [ 15, 0.00],
      [ 50, 0.55],
      [150, 1.00],
    ],
    shade: true,
    ambient: 0.20,
    diffuse: 0.80,
    specular: 0.32,
    specularPower: 14,
  },

  'custom-13-ct-bronchi-lumen': {
    name: '[13] Эндоскопия (Бронхи)',
    category: 'lung',
    modality: 'ct',
    color: [
      [-1000, 0.82, 0.52, 0.42],
      [ -800, 0.85, 0.55, 0.45],
      [ -400, 0.90, 0.60, 0.50],
      [    0, 0.95, 0.70, 0.60],
      [ 1000, 1.00, 0.80, 0.70],
    ],
    opacity: [
      [-1000, 0.00],
      [ -950, 0.00],
      [ -800, 0.32],
      [ -600, 0.62],
      [ -400, 0.82],
      [    0, 0.92],
      [ 1000, 1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 10, 0.00],
      [ 40, 0.60],
      [120, 1.00],
    ],
    shade: true,
    ambient: 0.28,
    diffuse: 0.72,
    specular: 0.10,
    specularPower: 5,
  },

  'custom-14-ct-aorta-stent': {
    name: '[14] Стенты / Металл',
    category: 'vascular',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [  150, 0.68, 0.18, 0.08],
      [  300, 0.88, 0.28, 0.18],
      [  600, 0.95, 0.88, 0.78],
      [ 1200, 1.00, 1.00, 0.58],
      [ 2500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [  120, 0.00],
      [  200, 0.12],
      [  350, 0.32],
      [  600, 0.82],
      [ 1200, 0.95],
      [ 3000, 1.00],
    ],
    gradient: null,
    shade: true,
    ambient: 0.15,
    diffuse: 0.85,
    specular: 0.55,
    specularPower: 32,
  },

  'custom-15-ct-muscle-skeletal': {
    name: '[15] Костно-мышечная',
    category: 'muscle',
    modality: 'ct',
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   20, 0.58, 0.18, 0.12],
      [   60, 0.78, 0.28, 0.18],
      [  150, 0.85, 0.48, 0.38],
      [  300, 0.90, 0.85, 0.75],
      [  800, 0.95, 0.95, 0.90],
      [ 1500, 1.00, 1.00, 1.00],
    ],
    opacity: [
      [-1000, 0.00],
      [    0, 0.00],
      [   30, 0.18],
      [   80, 0.35],
      [  150, 0.12],
      [  250, 0.42],
      [  500, 0.82],
      [ 1000, 0.95],
      [ 3000, 1.00],
    ],
    gradient: [
      [  0, 0.00],
      [ 15, 0.00],
      [ 50, 0.50],
      [150, 1.00],
    ],
    shade: true,
    ambient: 0.20,
    diffuse: 0.80,
    specular: 0.15,
    specularPower: 8,
  },

  // ══════════════════════════════════════════════════════════════════
  //  [11.1.15] ОРГАНЫ БРЮШНОЙ ПОЛОСТИ — по просьбе владельца.
  //
  //  ЧТО ЗДЕСЬ ВОЗМОЖНО, А ЧТО НЕТ. Кривая переноса различает только
  //  ПЛОТНОСТЬ. На нативном КТ печень 55–65 HU, селезёнка 40–50,
  //  поджелудочная 30–50, корковое вещество почки 30–45 — полосы
  //  перекрываются, и никакой пресет не отделит один орган от соседа.
  //  Разделяет их КОНТРАСТ: в портальную фазу селезёнка 110–140,
  //  поджелудочная 80–110 (пик в панкреатическую, 40–50 с), почка в
  //  нефрографическую 120–200. На эти окна пресеты и настроены.
  //  На нативе они показывают орган слабее и вместе с соседями — это
  //  предел метода, а не настройки.
  //
  //  Кость (>300 HU) в двух нижних пресетах ПОДАВЛЕНА: иначе рёбра и
  //  позвоночник забивают собой всю брюшную полость. У почек подавить
  //  нельзя — выведенный контраст 300–1000 HU лежит в той же полосе,
  //  что и кость, поэтому на урографии позвоночник виден всегда;
  //  отсекается он не кривой, а рамкой обрезки.
  // ══════════════════════════════════════════════════════════════════

  'custom-16-ct-pancreas': {
    name: '[16] Поджелудочная железа',
    category: 'gi',
    modality: 'ct',
    // [12.0.26] ПЛОТНОСТЬЮ ПОДЖЕЛУДОЧНУЮ НЕ ВЫДЕЛИТЬ. В любую фазу у неё то же
    // число, что у селезёнки, стенки кишки и желудка: панкреатическая фаза
    // 100–130 HU, портальная 90–110. Поэтому кнопка ВСЕГДА идёт в маску
    // (core/presetRouteCore.js). Эта кривая — запасной путь, когда модели
    // нет: она хотя бы не закрывает всё брюшной стенкой, но покажет железу
    // вместе с соседями той же плотности.
    phase: 'arterial',
    organs: ['pancreas'],
    suppress: ['bone', 'air', 'fat', 'muscle'],
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   75, 0.62, 0.52, 0.34],
      [  110, 0.93, 0.84, 0.60],   // пик панкреатической фазы — светлая охра
      [  150, 0.96, 0.91, 0.78],
      [  300, 0.86, 0.60, 0.54],   // сосуды рядом — приглушённо-красным
      [ 3000, 0.70, 0.70, 0.70],
    ],
    opacity: [
      // [12.0.26] ДО 75 HU — ПОЛНОСТЬЮ ПРОЗРАЧНО. Кожа, мышцы брюшной стенки и
      // диафрагма — 40–60 HU (с контрастом мышца добирает до 60–70). Прежняя
      // кривая набирала непрозрачность с 25–45 HU, и луч гас в первых
      // сантиметрах стенки: владелец прислал скрины — «мумия», рёбра едва
      // просвечивают. Работающие «Почки» и «Клапаны» прозрачны до 60 и 130 HU —
      // отсюда правило. Порог взят с запасом над мышцей по учебным
      // диапазонам, не по серии владельца.
      [-1000, 0.00],
      [   75, 0.00],
      [   95, 0.35],
      [  115, 0.75],               // панкреатическая фаза — максимум
      [  140, 0.55],               // дальше спорят селезёнка и почки
      [  190, 0.18],
      [  300, 0.05],
      [ 3000, 0.02],               // кость подавлена
    ],
    gradient: [
      [  0, 0.02],
      [  8, 0.20],
      [ 30, 0.70],
      [ 80, 1.00],                 // границы органа — резче, чем однородная паренхима
    ],
    shade: true,
    ambient: 0.24,
    diffuse: 0.76,
    specular: 0.12,
    specularPower: 6,
  },

  'custom-17-ct-spleen': {
    name: '[17] Селезёнка',
    category: 'gi',
    modality: 'ct',
    // [12.0.26] Селезёнка с контрастом: артериальная фаза 100–160 HU (пёстрая),
    // портальная 110–130. На артериальной печень 60–80 — ниже порога, и
    // селезёнка от неё отделяется. Почки (корковый слой 150–250) порогом не
    // отсечь — они будут в кадре. На нативе селезёнка 40–55 = мышца: не
    // показать, нужна маска.
    phase: 'portal',
    organs: ['spleen'],
    suppress: ['bone', 'air', 'fat', 'muscle'],
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   85, 0.52, 0.22, 0.30],
      [  115, 0.72, 0.32, 0.40],   // паренхима селезёнки
      [  150, 0.86, 0.48, 0.50],
      [  300, 0.95, 0.86, 0.76],
      [ 3000, 0.70, 0.70, 0.70],
    ],
    opacity: [
      // [12.0.26] ДО 85 HU — ПОЛНОСТЬЮ ПРОЗРАЧНО. Кожа, мышцы брюшной стенки и
      // диафрагма — 40–60 HU (с контрастом мышца добирает до 60–70). Прежняя
      // кривая набирала непрозрачность с 25–45 HU, и луч гас в первых
      // сантиметрах стенки: владелец прислал скрины — «мумия», рёбра едва
      // просвечивают. Работающие «Почки» и «Клапаны» прозрачны до 60 и 130 HU —
      // отсюда правило. Порог взят с запасом над мышцей по учебным
      // диапазонам, не по серии владельца.
      [-1000, 0.00],
      [   85, 0.00],               // и печень артериальной фазы (60–80) тоже
      [  100, 0.35],
      [  120, 0.80],               // селезёнка
      [  155, 0.72],
      [  220, 0.25],               // корковый слой почек — приглушённо
      [  400, 0.06],
      [ 3000, 0.03],               // кость подавлена
    ],
    gradient: [
      [  0, 0.03],
      [ 10, 0.22],
      [ 35, 0.72],
      [ 90, 1.00],
    ],
    shade: true,
    ambient: 0.23,
    diffuse: 0.77,
    specular: 0.14,
    specularPower: 7,
  },

  'custom-18-ct-kidney-native': {
    name: '[18] Почки (паренхима)',
    category: 'gi',
    modality: 'ct',
    // [12.0.26] Паренхима почки видна по КОНТРАСТУ: артериальная фаза —
    // корковый слой 150–250 HU, нефрографическая — вся паренхима 150–200.
    // Прежняя кривая обещала «натив 30–45 HU виден», но на нативе почка той
    // же плотности, что мышца: окно 25–45 HU и давало брюшную стенку во весь
    // кадр. Нативная паренхима — только маской. Губчатая кость (150–300)
    // проступит бледно, её порогом не отсечь.
    phase: 'nephrographic',
    organs: ['kidney_left', 'kidney_right'],
    suppress: ['bone', 'fat', 'muscle'],
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   75, 0.58, 0.26, 0.18],
      [  140, 0.84, 0.48, 0.28],   // корковый слой / нефрографическая фаза
      [  200, 0.94, 0.72, 0.38],
      [  350, 0.80, 0.60, 0.45],
      [ 3000, 0.60, 0.60, 0.60],
    ],
    opacity: [
      // [12.0.26] ДО 75 HU — ПОЛНОСТЬЮ ПРОЗРАЧНО. Кожа, мышцы брюшной стенки и
      // диафрагма — 40–60 HU (с контрастом мышца добирает до 60–70). Прежняя
      // кривая набирала непрозрачность с 25–45 HU, и луч гас в первых
      // сантиметрах стенки: владелец прислал скрины — «мумия», рёбра едва
      // просвечивают. Работающие «Почки» и «Клапаны» прозрачны до 60 и 130 HU —
      // отсюда правило. Порог взят с запасом над мышцей по учебным
      // диапазонам, не по серии владельца.
      [-1000, 0.00],
      [   75, 0.00],
      [  100, 0.20],
      [  140, 0.72],               // паренхима с контрастом
      [  200, 0.82],
      [  270, 0.45],
      [  350, 0.12],               // аорта и выведенный контраст — приглушённо
      [  600, 0.04],
      [ 3000, 0.02],               // кость подавлена
    ],
    gradient: [
      [  0, 0.03],
      [ 10, 0.25],
      [ 40, 0.75],
      [100, 1.00],
    ],
    shade: true,
    ambient: 0.24,
    diffuse: 0.76,
    specular: 0.12,
    specularPower: 6,
  },

  // ══════════════════════════════════════════════════════════════════
  // [12.0.18] ФАЗОВЫЕ И КОМБИНИРОВАННЫЕ ОРГАННЫЕ ПРЕСЕТЫ
  //
  // Числа HU здесь — не подбор на глаз, а клинические диапазоны:
  //   портальная (65–70 с): селезёнка 90–120, поджелудочная 80–110,
  //                         печень 90–110, корковое вещество почки 120–180
  //   артериальная:         аорта 250–400, поджелудочная 90–130
  //   экскреторная:         ЧЛС и мочеточники > 300 — выводятся отдельно
  //
  // ЧЕГО ЭТИ ПРЕСЕТЫ НЕ МОГУТ, И ЭТО НЕ ЛЕЧИТСЯ ТОЧКАМИ КРИВОЙ. Диапазоны
  // соседних органов перекрываются: на нативе селезёнка 40–55 и мышца 40–55 —
  // ОДНО И ТО ЖЕ ЧИСЛО. Кривая «плотность → прозрачность» не знает, где
  // воксель, поэтому вместе с органом обязана показать соседа той же
  // плотности. Для раздельного показа есть режим «по маске»
  // (core/organIsolate3D.js): там работает форма, а не плотность.
  // ══════════════════════════════════════════════════════════════════

  'custom-19-ct-kidney-excretory': {
    name: '[19] Почки (экскреторная, без ЧЛС)',
    category: 'gi',
    modality: 'ct',
    // [12.0.26] Экскреторная фаза: паренхима 100–150 HU, ЧЛС и мочеточники
    // с контрастом 300–1000+ — гасим, иначе слепок закроет паренхиму.
    phase: 'excretory',
    organs: ['kidney_left', 'kidney_right'],
    suppress: ['bone', 'fat', 'muscle', 'collecting-system'],
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   75, 0.55, 0.26, 0.18],
      [  140, 0.88, 0.55, 0.32],   // паренхима, экскреторная фаза
      [  260, 0.72, 0.48, 0.34],
      [  400, 0.45, 0.35, 0.30],   // ЧЛС — тускло
      [ 3000, 0.40, 0.40, 0.40],
    ],
    opacity: [
      // [12.0.26] ДО 75 HU — ПОЛНОСТЬЮ ПРОЗРАЧНО. Кожа, мышцы брюшной стенки и
      // диафрагма — 40–60 HU (с контрастом мышца добирает до 60–70). Прежняя
      // кривая набирала непрозрачность с 25–45 HU, и луч гас в первых
      // сантиметрах стенки: владелец прислал скрины — «мумия», рёбра едва
      // просвечивают. Работающие «Почки» и «Клапаны» прозрачны до 60 и 130 HU —
      // отсюда правило. Порог взят с запасом над мышцей по учебным
      // диапазонам, не по серии владельца.
      [-1000, 0.00],
      [   75, 0.00],
      [   95, 0.30],
      [  120, 0.65],
      [  150, 0.80],               // паренхима
      [  240, 0.55],
      [  300, 0.10],               // ЧЛС (> 300 HU) подавлена
      [  600, 0.02],
      [ 3000, 0.02],
    ],
    gradient: [
      [  0, 0.03],
      [ 10, 0.25],
      [ 40, 0.75],
      [100, 1.00],
    ],
    shade: true,
    ambient: 0.24,
    diffuse: 0.76,
    specular: 0.12,
    specularPower: 6,
  },

  'custom-20-ct-kidneys-spleen': {
    name: '[20] Почки и селезёнка',
    category: 'gi',
    modality: 'ct',
    // [12.0.26] Оба органа — по контрасту: селезёнка 100–160 HU, корковый
    // слой почек 150–250. Окно 85–260: мышца и печень артериальной фазы ниже,
    // аорта и кость выше приглушены.
    phase: 'portal',
    organs: ['kidney_left', 'kidney_right', 'spleen'],
    suppress: ['bone', 'fat', 'air', 'muscle'],
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   85, 0.52, 0.24, 0.30],
      [  115, 0.72, 0.32, 0.40],   // селезёнка
      [  170, 0.88, 0.52, 0.34],   // корковый слой почек
      [  260, 0.70, 0.52, 0.40],
      [ 3000, 0.50, 0.50, 0.50],
    ],
    opacity: [
      // [12.0.26] ДО 85 HU — ПОЛНОСТЬЮ ПРОЗРАЧНО. Кожа, мышцы брюшной стенки и
      // диафрагма — 40–60 HU (с контрастом мышца добирает до 60–70). Прежняя
      // кривая набирала непрозрачность с 25–45 HU, и луч гас в первых
      // сантиметрах стенки: владелец прислал скрины — «мумия», рёбра едва
      // просвечивают. Работающие «Почки» и «Клапаны» прозрачны до 60 и 130 HU —
      // отсюда правило. Порог взят с запасом над мышцей по учебным
      // диапазонам, не по серии владельца.
      [-1000, 0.00],
      [   85, 0.00],
      [  100, 0.35],
      [  120, 0.78],               // селезёнка
      [  180, 0.80],               // корковый слой почек
      [  260, 0.35],
      [  400, 0.06],
      [ 3000, 0.03],               // кость подавлена
    ],
    gradient: [
      [  0, 0.03],
      [ 10, 0.24],
      [ 38, 0.74],
      [ 95, 1.00],
    ],
    shade: true,
    ambient: 0.23,
    diffuse: 0.77,
    specular: 0.13,
    specularPower: 7,
  },

  'custom-21-ct-upper-abdomen-organs': {
    // [12.0.35] Имя — как у кнопки. Прежнее «Поджелудочная, селезёнка, почки»
    // устарело с 12.0.32 (в набор добавлена печень) и попадало в плашку
    // «Пресет «…» подогнан под шкалу серии».
    name: '[21] Органы брюшной полости',
    category: 'gi',
    modality: 'ct',
    // [12.0.26] В наборе есть поджелудочная — её плотностью не выделить, и
    // кнопка ВСЕГДА идёт в маску (core/presetRouteCore.js). Кривая — запасной
    // путь без модели: контрастные органы 85–260 HU без брюшной стенки.
    // Прежняя требовала «накрыть поджелудочную на нативе (32 HU)» — это и
    // была мышца во весь кадр на скрине владельца.
    phase: 'portal',
    organs: ['liver', 'pancreas', 'spleen', 'kidney_left', 'kidney_right'],   // [12.0.32] + печень
    suppress: ['bone', 'fat', 'air', 'muscle'],
    color: [
      [-1000, 0.00, 0.00, 0.00],
      [   85, 0.62, 0.46, 0.26],
      [  110, 0.78, 0.40, 0.42],   // селезёнка / поджелудочная
      [  170, 0.86, 0.50, 0.33],   // корковый слой почек
      [  280, 0.68, 0.54, 0.44],
      [ 3000, 0.50, 0.50, 0.50],
    ],
    opacity: [
      // [12.0.26] ДО 85 HU — ПОЛНОСТЬЮ ПРОЗРАЧНО. Кожа, мышцы брюшной стенки и
      // диафрагма — 40–60 HU (с контрастом мышца добирает до 60–70). Прежняя
      // кривая набирала непрозрачность с 25–45 HU, и луч гас в первых
      // сантиметрах стенки: владелец прислал скрины — «мумия», рёбра едва
      // просвечивают. Работающие «Почки» и «Клапаны» прозрачны до 60 и 130 HU —
      // отсюда правило. Порог взят с запасом над мышцей по учебным
      // диапазонам, не по серии владельца.
      [-1000, 0.00],
      [   85, 0.00],
      [  100, 0.35],
      [  120, 0.74],
      [  175, 0.74],               // корковый слой почек в портальную
      [  270, 0.26],
      [  420, 0.05],
      [ 3000, 0.03],               // кость подавлена
    ],
    gradient: [
      [  0, 0.03],
      [ 10, 0.22],
      [ 36, 0.72],
      [ 95, 1.00],
    ],
    shade: true,
    ambient: 0.24,
    diffuse: 0.76,
    specular: 0.12,
    specularPower: 7,
  },
};

// ══════════════════════════════════════════════════════════════════
// 4. VALIDATION ON INIT
// ══════════════════════════════════════════════════════════════════

try {
  for (const [key, preset] of Object.entries(_PRESETS_CONFIG)) {
    validatePreset(preset, key);
  }
  log.info(`[Presets3D] ✅ All ${Object.keys(_PRESETS_CONFIG).length} presets validated`);
} catch (e) {
  log.error('[Presets3D] ❌ Preset validation failed:', (e as Error).message);
  throw e;
}

// ══════════════════════════════════════════════════════════════════
// 5. MAPS & FROZEN STATE
// ══════════════════════════════════════════════════════════════════

const _customPresets = new Map<string, Preset3D>();

export const PRESETS_3D = deepFreeze(_PRESETS_CONFIG);

export const PRESET_ALIAS = deepFreeze({
  'ct-bone-3d':   'ct-bone-clean',
  'ct-skin':      'ct-skin',
  'ct-muscle':    'ct-muscle',
  'ct-vascular':  'ct-vascular',
  'ct-air':       'ct-airways',
  'ct-lung-3d':   'ct-lung-parenchyma',
  'ct-colon':     'ct-colon',
  'mri-brain-3d': 'mri-brain',
  'mri-wm-gm':    'mri-wm-gm',
  'cardiac-3d':   'cardiac-3d',
  'angio-3d':     'angio-3d',
  'fetus':        'fetus',
} as const);

// ══════════════════════════════════════════════════════════════════
// 6. ENCAPSULATED REGISTRY (Singleton Pattern)
// ══════════════════════════════════════════════════════════════════

class PresetRegistry {
  #presets: Readonly<Record<string, Preset3D>>;
  #aliases: Readonly<Record<string, string>>;
  #allPresetsCache: ReadonlyArray<Readonly<{ key: string; name: string; category: string; modality?: string }>>;
  #categoryCache = new Map<string, ReadonlyArray<Readonly<{ key: string; name: string; preset: Preset3D }>>>();
  #modalityCache = new Map<string, ReadonlyArray<Readonly<{ key: string; name: string; preset: Preset3D }>>>();

  constructor(presets: Record<string, Preset3D>, aliases: Record<string, string>) {
    this.#presets = presets;
    this.#aliases = aliases;
    
    // Pre-compute cache для оптимизации производительности
    this.#allPresetsCache = Object.freeze(
      Object.entries(presets).map(([key, p]) => 
        Object.freeze({ key, name: p.name, category: p.category, modality: p.modality })
      )
    );
  }

  resolve(key: string): Preset3D | undefined {
    const resolvedKey = this.#aliases[key] ?? key;
    return this.#presets[resolvedKey] ?? _customPresets.get(resolvedKey);
  }

  listAll(): ReadonlyArray<Readonly<{ key: string; name: string; category: string; modality?: string }>> {
    return this.#allPresetsCache;
  }

  getByCategory(category: string): ReadonlyArray<Readonly<{ key: string; name: string; preset: Preset3D }>> {
    const cached = this.#categoryCache.get(category);
    if (cached) return cached;

    const result = Object.freeze(
      Object.entries(this.#presets)
        .filter(([_, p]) => p.category === category)
        .map(([key, p]) => Object.freeze({ key, name: p.name, preset: p }))
    );
      
    this.#categoryCache.set(category, result);
    return result;
  }

  getByModality(modality: string): ReadonlyArray<Readonly<{ key: string; name: string; preset: Preset3D }>> {
    const cached = this.#modalityCache.get(modality);
    if (cached) return cached;

    const result = Object.freeze(
      Object.entries(this.#presets)
        .filter(([_, p]) => p.modality === modality || p.modality === 'any')
        .map(([key, p]) => Object.freeze({ key, name: p.name, preset: p }))
    );
      
    this.#modalityCache.set(modality, result);
    return result;
  }

  addCustomPreset(key: string, preset: Preset3D): Result<void, Error> {
    try {
      validatePreset(preset, key);
      _customPresets.set(key, preset);
      log.info(`[Presets3D] ✅ Custom preset added: ${preset.name} (${key})`);
      return Ok(undefined);
    } catch (err) {
      return Err(err instanceof Error ? err : new Error(String(err)));
    }
  }

  removeCustomPreset(key: string): boolean {
    const removed = _customPresets.delete(key);
    if (removed) {
      log.info(`[Presets3D] ✅ Custom preset removed: ${key}`);
    }
    return removed;
  }

  invalidateCache(): void {
    this.#categoryCache.clear();
    this.#modalityCache.clear();
  }
}

export const registry = new PresetRegistry(PRESETS_3D, PRESET_ALIAS);

// ══════════════════════════════════════════════════════════════════
// 7. VTK ADAPTER ENGINE (Safe & Strict)
// ══════════════════════════════════════════════════════════════════

class VtkPresetEngine {
  static apply(actor: IVtkVolumeActor, preset: Preset3D): Result<void, Error> {
    const property = actor.getProperty?.() ?? actor.property;
    if (!property) {
      return Err(new Error('VTK VolumeProperty is undefined or missing.'));
    }

    try {
      this.#applyColor(property, preset.color);
      this.#applyOpacity(property, preset.opacity);
      this.#applyGradient(property, preset.gradient);
      this.#applyShading(property, preset);
      return Ok(undefined);
    } catch (err) {
      return Err(err instanceof Error ? err : new Error(String(err)));
    }
  }

  static #resolveTransferFunction(
    getter: (() => IVtkTransferFunction | IVtkTransferFunction[] | undefined) | undefined
  ): IVtkTransferFunction | null {
    if (!getter) return null;
    const fn = getter();
    if (Array.isArray(fn)) return fn[0] ?? null;
    return fn && typeof fn.removeAllPoints === 'function' ? fn : null;
  }

  static #applyColor(property: IVtkVolumeProperty, points: readonly ColorPoint[]): void {
    const ctfun = this.#resolveTransferFunction(property.getRGBTransferFunction?.bind(property));
    if (!ctfun) return;

    ctfun.removeAllPoints();
    for (const [hu, r, g, b] of points) {
      ctfun.addRGBPoint(hu, r, g, b);
    }
  }

  static #applyOpacity(property: IVtkVolumeProperty, points: readonly OpacityPoint[]): void {
    const ofun = property.getScalarOpacity?.(0);
    if (!ofun || typeof ofun.removeAllPoints !== 'function') return;

    ofun.removeAllPoints();
    for (const [hu, alpha] of points) {
      // [11.1.23] Глобальный масштаб непрозрачности («Плотность тканей»).
      // Владелец заметил: при вращении органы видны ЛУЧШЕ, чем в покое.
      // Физика такова: vtk во время взаимодействия увеличивает шаг луча
      // (autoAdjustSampleDistances), выборок на луч меньше — накопленная
      // непрозрачность падает — оболочка мышц/кожи истончается и интерьер
      // проступает. Красивый «вид при вращении» — это недосэмплированный
      // рендер. Масштаб ниже даёт ТОТ ЖЕ эффект честно и управляемо, без
      // потери качества выборки, на любом пресете.
      ofun.addPoint(hu, Math.min(1, alpha * _множитель(hu)));   // [11.1.47] гасим избирательно
    }
    // [11.1.25→11.1.36] Кривая регистрируется В РЕЕСТРЕ (по своей функции):
    // «Плотность тканей» правит все живые окна сразу, а не одно последнее.
    const свой = _кривые.findIndex((к) => к.ofun === ofun);
    if (свой >= 0) _кривые[свой] = { ofun, points };
    else { _кривые.push({ ofun, points }); if (_кривые.length > 12) _кривые.shift(); }
  }

  static #applyGradient(property: IVtkVolumeProperty, points?: readonly GradientPoint[] | null): void {
    if (!property.getGradientOpacity || !property.setUseGradientOpacity) return;

    try {
      const gofun = property.getGradientOpacity(0);
      if (points && gofun && typeof gofun.removeAllPoints === 'function') {
        gofun.removeAllPoints();
        for (const [mag, alpha] of points) {
          gofun.addPoint(mag, alpha);
        }
        property.setUseGradientOpacity(0, true);
      } else {
        property.setUseGradientOpacity(0, false);
      }
    } catch {
      property.setUseGradientOpacity(0, false);
    }
  }

  static #applyShading(property: IVtkVolumeProperty, preset: Preset3D): void {
    const setSafe = <K extends keyof IVtkVolumeProperty>(method: K, value: Parameters<IVtkVolumeProperty[K]>[0]) => {
      const fn = property[method];
      if (typeof fn === 'function') {
        (fn as Function).call(property, value);
      }
    };

    setSafe('setShade', preset.shade ?? true);
    setSafe('setAmbient', preset.ambient ?? 0.2);
    setSafe('setDiffuse', preset.diffuse ?? 0.8);
    setSafe('setSpecular', preset.specular ?? 0.2);
    setSafe('setSpecularPower', preset.specularPower ?? 10);
    
    if (typeof property.setScalarOpacityUnitDistance === 'function') {
      property.setScalarOpacityUnitDistance(0, preset.opacityUnitDistance ?? 4.0);
    }
  }
}

// ══════════════════════════════════════════════════════════════════
// 7b. ГЛОБАЛЬНЫЙ МАСШТАБ НЕПРОЗРАЧНОСТИ [11.1.23]
// ══════════════════════════════════════════════════════════════════

let _масштабНепрозрачности = 1;
/**
 * [11.1.47] ИЗБИРАТЕЛЬНОЕ ГАШЕНИЕ. «Плотность тканей» умножает ВСЮ кривую —
 * бледнеет и жир, и паренхима; владелец справедливо заметил, что органы
 * тускнеют вместе со стенкой, а я об этом умалчивал. Здесь — гашение
 * ТОЛЬКО ниже порога плотности: мягкие ткани уходят в прозрачность, а
 * паренхима и контрастированные сосуды остаются во весь голос и на их фоне
 * выглядят темнее и резче.
 *
 * порогHU — граница «мягкое / орган» (по умолчанию 70 HU: жир −100…−50,
 * мышца 40…60 остаются ниже; печень 60…80, селезёнка с контрастом 100…140,
 * сосуды 200+ — выше). переходHU — ширина плавного перехода, чтобы граница
 * не резала ткань ступенькой.
 */
let _мягкость = { коэф: 1, порогHU: 70, переходHU: 25 };

/** Множитель непрозрачности для конкретной плотности. */
function _множитель(hu: number): number {
  const { коэф, порогHU, переходHU } = _мягкость;
  if (коэф >= 1) return _масштабНепрозрачности;
  const низ = порогHU - переходHU;
  let доля: number;
  if (hu <= низ) доля = 1;                                   // целиком мягкая ткань
  else if (hu >= порогHU) доля = 0;                          // целиком орган
  else доля = (порогHU - hu) / (порогHU - низ);              // плавный переход
  return _масштабНепрозрачности * (коэф * доля + 1 * (1 - доля));
}
/**
 * [11.1.36] РЕЕСТР кривых вместо одной «последней». Одна переменная и была
 * ямой: у КАЖДОГО объёмного окна свой актёр и своя функция непрозрачности, а
 * помнили мы только последнюю применённую. Врач смотрит «3D пресет»
 * (шестое окно MPR6), двигает «Плотность тканей» — а ползунок честно мутирует
 * кривую ДРУГОГО окна и возвращает true: «движется, но ничего не делает»
 * (замечание владельца, телефон). Теперь помним кривые всех живых окон и
 * масштабируем каждую; мёртвые (без removeAllPoints) выбрасываются на ходу.
 */
let _кривые: Array<{ ofun: any; points: readonly OpacityPoint[] }> = [];

/**
 * Задать масштаб 0.05…1.
 * @returns {boolean} применилось ли ПРЯМО СЕЙЧАС к живому объёму (вызывающему
 * остаётся только перерисовать окна). false — объём ещё не построен, масштаб
 * ляжет с ближайшим пресетом.
 */
/**
 * [11.1.47] Прозрачность МЯГКИХ ТКАНЕЙ отдельно от органов.
 * @param коэф 0.05…1 — во сколько раз гасить всё, что ниже порога (1 = не гасить)
 * @param порогHU граница «мягкое/орган», 0…400
 */
export function setSoftTissueScale(коэф: number, порогHU?: number): boolean {
  const k = Number(коэф);
  _мягкость.коэф = Number.isFinite(k) ? Math.min(1, Math.max(0.05, k)) : 1;
  const п = Number(порогHU);
  if (Number.isFinite(п)) _мягкость.порогHU = Math.min(400, Math.max(0, п));
  return setGlobalOpacityScale(_масштабНепрозрачности);   // пересчитать все живые кривые
}

export function setGlobalOpacityScale(х: number): boolean {
  const v = Number(х);
  _масштабНепрозрачности = Number.isFinite(v) ? Math.min(1, Math.max(0.05, v)) : 1;
  let легло = 0;
  _кривые = _кривые.filter((к) => {
    if (!к?.ofun?.removeAllPoints) return false;   // мёртвая — выбросить
    try {
      к.ofun.removeAllPoints();
      for (const [hu, alpha] of к.points) {
        к.ofun.addPoint(hu, Math.min(1, alpha * _множитель(hu)));
      }
      легло++;
      return true;
    } catch {
      return false;   // функция умерла между кадрами — выбросить
    }
  });
  return легло > 0;
}

export function getGlobalOpacityScale(): number {
  return _масштабНепрозрачности;
}

// ══════════════════════════════════════════════════════════════════
// 8. PUBLIC API (Result Pattern)
// ══════════════════════════════════════════════════════════════════

export function applyCustomPreset(viewport: IViewport, presetKey: string): Result<boolean, Error> {
  const preset = registry.resolve(presetKey);

  if (!preset) {
    return Err(new Error(`Unknown preset requested: "${presetKey}"`));
  }

  try {
    const actorEntry = viewport.getDefaultActor?.();
    let actor = actorEntry?.actor;
    
    if (!actor) {
      const actors = viewport.getActors?.();
      if (actors && actors.length > 0) {
        const first = actors[0];
        actor = 'actor' in first ? first.actor : first;
      }
    }
    
    if (!actor) {
      return Err(new Error('Volume actor not found in viewport.'));
    }

    const result = VtkPresetEngine.apply(actor, preset);
    if (!result.success) return result;

    viewport.render();
    log.debug(`[Presets3D] ✅ Preset applied: ${preset.name} (${presetKey})`);
    return Ok(true);

  } catch (err) {
    return Err(err instanceof Error ? err : new Error(String(err)));
  }
}

/**
 * [10.0.3] Достать сам пресет, а не только его имя.
 *
 * Нужно, чтобы перенести точки пресета на шкалу конкретной серии: у КТ это
 * HU и точки осмысленны как есть, а у МР шкала своя, и «кожа» с окном
 * −200…+200 HU на МР-данных попадает мимо всего — окно чернеет.
 */
export const getPreset3D = (key: string): Preset3D | undefined => registry.resolve(key);

export const listAllPresets = (): ReturnType<typeof registry.listAll> => registry.listAll();
export const getPresetsByCategory = (cat: string): ReturnType<typeof registry.getByCategory> => registry.getByCategory(cat);
export const getPresetsByModality = (modality: string): ReturnType<typeof registry.getByModality> => registry.getByModality(modality);
export const addCustomPreset = (key: string, preset: Preset3D): ReturnType<typeof registry.addCustomPreset> => registry.addCustomPreset(key, preset);
export const removeCustomPreset = (key: string): boolean => registry.removeCustomPreset(key);
export const invalidatePresetCache = (): void => registry.invalidateCache();